import React, { useState } from 'react'
import { strengthenImageUrl } from '../utils/formatting'

export interface UnknownImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** The image source URL - can be IPFS, HTTP, or raw hash */
  src: string
  /** Whether to wrap the image in an anchor link to the source */
  asLink?: boolean
}

/**
 * An image component that handles various URL formats including IPFS.
 * Automatically converts IPFS URIs and hashes to gateway URLs.
 * 
 * @example
 * <UnknownImage src="ipfs://QmHash123" alt="My image" className="w-10 h-10" />
 */
export const UnknownImage = ({
  asLink = false,
  src,
  ...props
}: UnknownImageProps) => {
  if (!src) return null
  const [numberOfErrors, setNumberOfErrors] = useState(0)

  const onImageError = (
    e: React.SyntheticEvent<HTMLImageElement>,
    finalSrc: string
  ) => {
    setNumberOfErrors(numberOfErrors + 1)
    // Only log in development
    if (typeof window !== 'undefined' && (window as any).__DEV__) {
      console.log('UnknownImage error:', e.currentTarget.src, finalSrc)
    }
  }

  const finalSrc = strengthenImageUrl(src)

  const img = (
    <img src={finalSrc} onError={(e) => onImageError(e, finalSrc)} {...props} />
  )

  if (asLink) {
    return (
      <a href={finalSrc} target="_blank" rel="noopener noreferrer">
        {img}
      </a>
    )
  }

  return img
}

export default UnknownImage

